/* eslint-disable no-nested-ternary */
// eslint-disable-next-line no-nested-ternary

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import {
  AmountInput,
  Button,
  Icon,
  Input,
  Modal,
  RadioGroup
} from '@components/index';
import Spinner from '@components/spinner/Spinner';
import { ErrorCode } from '@constants/errorCodes';
import {
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { GROUPS, SUBSCRIPTIONS } from '@constants/routes';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  useClosePaymentMutation,
  useLazyUpdateDueQuery
} from '@features/transactions/queires/TransactionsQuery';
import useNotify from '@hooks/notify';
import {
  ApiError,
  UpdateDueAmountsResponse,
  UpdateDueModalProps
} from '@types';

import theme from '../../../../../themes/colors';

const UpdateDueModal = ({
  isOpen,
  handleCancel,
  transactionData,
  onSuccessUpdate,
  handleUpdateSelectedAction,
  tabName,
  organizationId,
  branchId,
  orderId,
  onCustomClosePayment
}: UpdateDueModalProps) => {
  const { currentSubscription, rolesAndPermissions } = useSelector(
    (state: any) => state.user
  );
  const { createErrorAlert, createSuccessAlert } = useNotify();
  const navigate = useNavigate();

  const hasPaidAmount = Number(transactionData.total_paid) > 0;
  const pendingAmount =
    Number(transactionData.total_fee) - Number(transactionData.total_paid);
  const [discountAmount, setDiscountAmount] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [discountAmountError, setDiscountAmountError] =
    useState<React.ReactNode>('');

  const [afterDiscountData, setAfterDiscountData] =
    useState<UpdateDueAmountsResponse | null>(null);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [closePayment, { isLoading: isClosePaymentLoading }] =
    useClosePaymentMutation();
  const [updateDueAmounts, { isLoading: isUpdateDueAmountsLoading }] =
    useLazyUpdateDueQuery();

  const actionOptions = [
    {
      key: 'close_payment',
      value: 'Close Payment',
      id: 'close_payment',
      disabled: !hasPaidAmount,
      tooltipText: !hasPaidAmount
        ? 'Only partially paid links can be closed.'
        : ''
    },
    {
      key: 'discount',
      value: 'Discount',
      id: 'discount',
      disabled: false,
      tooltipText: ''
    }
  ];

  const getInfoMessage = () => {
    if (transactionData.action === 'close_payment') {
      return (
        <div className="leading-[20px] tracking-[-0.54px] text-cofeeNeutral10">
          The due amount will be updated to
          <span className="mx-1 inline-flex items-baseline">
            <CurrencyAmount value={0} symbolClass="!mr-0.5" />
          </span>
          and no further changes can be done.
        </div>
      );
    }
    return '';
  };

  const fetchAfterDiscountAmounts = useCallback(
    async (amount: string) => {
      if (!orderId || Number(amount) <= 0) {
        setAfterDiscountData(null);
        return;
      }
      const res = await updateDueAmounts({
        params: { organizationId, branchId, orderId },
        discountAmount: amount
      });
      if ('data' in res && res.data) {
        setAfterDiscountData(res.data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderId]
  );

  const validateDiscountAmount = (value: string) => {
    if (Number(value) < 100) {
      setDiscountAmountError(
        <>
          The discount amount must be
          <span className="mx-1 inline-flex items-baseline">
            <CurrencyAmount value={100} symbolClass="!mr-0.5" />
          </span>
          or more
        </>
      );
      setAfterDiscountData(null);
    } else if (Number(pendingAmount) - Number(value) < 100) {
      setDiscountAmountError(
        <>
          Please ensure the due amount remains
          <span className="mx-1 inline-flex items-baseline">
            <CurrencyAmount value={100} symbolClass="!mr-0.5" />
          </span>
          or more.
        </>
      );
      setAfterDiscountData(null);
    } else {
      setDiscountAmountError('');
    }
  };

  const handleOnDiscountAmountBlur = () => {
    validateDiscountAmount(discountAmount);
  };

  const handleOnDiscountAmountChange = (value: string) => {
    setDiscountAmount(value);
    validateDiscountAmount(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (Number(value) > 0) {
      debounceRef.current = setTimeout(() => {
        fetchAfterDiscountAmounts(value);
      }, 500);
    } else {
      setAfterDiscountData(null);
    }
  };

  const handleClosePayment = async () => {
    sendMixpanelEvent(Event.TRANSACTION_CLOSE_PAYMENT_MODAL_CONFIRM_CLICKED, {
      paymentOrderId: organizationId,
      paymentDescription: note
    });
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const res = onCustomClosePayment
        ? await onCustomClosePayment({ note })
        : await closePayment({
            params: {
              organizationId,
              branchId,
              orderId
            },
            body: {
              note
            }
          });
      if ('data' in res) {
        handleCancel();
        onSuccessUpdate();
        createSuccessAlert('Payment closed successfully');
      } else {
        handleCancel();
        const apiError = res.error as ApiError;
        createErrorAlert(
          apiError?.data?.message || 'Error in closing the payment'
        );
        if (apiError?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
          if (
            rolesAndPermissions.permissions.includes(
              UserPermissionTypes.SUBSCRIPTION_VIEW
            )
          ) {
            navigate(SUBSCRIPTIONS.PLAN_DETAILS);
          } else {
            navigate(GROUPS.HOME);
          }
        }
      }
    }
  };

  const handleConfirmButtonClick = () => {
    sendMixpanelEvent(Event.TRANSACTION_UPDATE_DUE_MODAL_CONFIRM_CLICKED, {
      paymentOrderId: transactionData.id,
      transactionMemberName: transactionData.name,
      transactionMemberAmount: transactionData.total_fee,
      transactionMemberPaidAmount: transactionData.total_paid,
      action: transactionData.action,
      discountAmount,
      note,
      tab: tabName
    });
    if (transactionData.action === 'discount') {
      onSuccessUpdate({ discount_amount: discountAmount, note });
    } else {
      handleClosePayment();
    }
  };

  useEffect(() => {
    setDiscountAmountError('');
    setDiscountAmount('');
    setAfterDiscountData(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, [transactionData.action]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  return (
    <Modal isOpen={isOpen} onCancel={handleCancel} className="!rounded-[16px]">
      <div className="flex w-[525px] flex-col bg-white">
        <div className="flex flex-row items-center justify-between px-6 py-4 text-xl font-semibold leading-[28px] tracking-normal">
          Update due amount
          <Button
            variant="transparent"
            handleClick={handleCancel}
            data-testid="update-due-modal-close"
          >
            <Icon name="close" size="mediumlarge" />
          </Button>
        </div>
        <div className="border-y border-borderGrey7 p-6">
          <div className="flex w-full flex-row rounded-lg border border-cofeeNeutral4 bg-cofeeNeutral2 py-3">
            <div className="flex-1 border-r border-borderGrey7 px-4 text-center">
              <div className="font-sm pb-[6px] font-medium leading-[100%] tracking-normal text-cofeeNeutral8">
                Total Fee
              </div>
              <div className="flex flex-row items-center justify-center text-base font-medium leading-[100%] tracking-normal text-cofeeNeutral10">
                <CurrencyAmount
                  value={Number(
                    transactionData.original_total_amount_requested
                  )}
                  valueClass="pl-1"
                  symbolClass="!font-normal"
                />
              </div>
            </div>
            <div className="flex-1 border-r border-borderGrey7 px-4 text-center">
              <div className="font-sm pb-[6px] font-medium leading-[100%] tracking-normal text-cofeeGreen7">
                Paid
              </div>
              <div className="flex flex-row items-center justify-center text-base font-medium leading-[100%] tracking-normal text-cofeeNeutral10">
                <CurrencyAmount
                  value={Number(transactionData.total_paid)}
                  valueClass="pl-1"
                  symbolClass="!font-normal"
                />
              </div>
            </div>
            {transactionData.total_discount_amount &&
              Number(transactionData.total_discount_amount) > 0 && (
                <div className="flex-1 border-r border-borderGrey7 px-4 text-center">
                  <div className="font-sm pb-[6px] font-medium leading-[100%] tracking-normal text-cofeeBlue400">
                    Discount
                  </div>
                  <div className="flex flex-row items-center justify-center text-base font-medium leading-[100%] tracking-normal text-cofeeNeutral10">
                    <CurrencyAmount
                      value={Number(transactionData.total_discount_amount)}
                      valueClass="pl-1"
                      symbolClass="!font-normal"
                    />
                  </div>
                </div>
              )}
            <div className="flex-1 px-4 text-center">
              <div className="font-sm pb-[6px] font-medium leading-[100%] tracking-normal text-cofeeRed7">
                Pending
              </div>
              <div className="flex flex-row items-center justify-center text-base font-medium leading-[100%] tracking-normal text-cofeeNeutral10">
                <CurrencyAmount
                  value={Number(pendingAmount)}
                  valueClass="pl-1"
                  symbolClass="!font-normal"
                />
              </div>
            </div>
          </div>
          <div className="pb-[8px] pt-4 text-[14px] font-semibold leading-[20px] tracking-[-0.2px] text-cofeeNeutral10">
            Select an action
          </div>
          <RadioGroup
            options={actionOptions}
            selectedValue={transactionData.action}
            handleChange={(value) =>
              handleUpdateSelectedAction(value as 'close_payment' | 'discount')
            }
            orientation="horizontal"
            iconStyle="accent-primary110 !size-5"
            labelStyle="!text-base font-semibold leading-[100%] tracking-normal !text-cofeeNeutral10"
            customTooltipStyle="!left-0 !top-[-42px]"
          />
          {transactionData.action === 'discount' && (
            <div className="pt-4">
              <div className="pb-1 text-sm font-semibold leading-5 tracking-[-0.2px] text-cofeeNeutral10">
                Enter discount amount
              </div>
              <AmountInput
                value={discountAmount}
                onAmountChange={handleOnDiscountAmountChange}
                placeholder="Enter discount amount"
                className="!bg-white"
                error={!!discountAmountError}
                errorMessage={discountAmountError}
                handleInputBlur={handleOnDiscountAmountBlur}
                maxAmount={pendingAmount.toString() || '0'}
              />
            </div>
          )}
          {transactionData.action === 'discount' &&
          isUpdateDueAmountsLoading ? (
            <div className="flex min-h-30 items-center justify-center rounded-lg border border-cofeeNeutral4 p-6">
              <Spinner size="lg" />
            </div>
          ) : transactionData.action === 'discount' &&
            Number(discountAmount) > 0 &&
            afterDiscountData &&
            !discountAmountError ? (
            <div className="mt-4">
              <div className="pb-2 text-sm font-semibold leading-[20px] tracking-[-0.2px] text-cofeeNeutral10">
                After discount
              </div>
              {afterDiscountData && (
                <div className="rounded-lg border border-cofeeNeutral4 bg-[#F4F9FF]">
                  <div className="flex items-center justify-between border-b border-borderGrey7 p-3">
                    <div className="text-sm font-medium text-cofeeRed7">
                      New pending
                    </div>
                    <div className="text-sm font-medium text-cofeeRed7">
                      <CurrencyAmount
                        value={Number(afterDiscountData.pending_amount)}
                        symbolClass="!mr-0.5"
                      />
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    {Number(afterDiscountData.fee_amount) > 0 && (
                      <div className="flex items-center justify-between py-1">
                        <div className="text-sm font-medium text-cofeeNeutral8">
                          Fee amount
                        </div>
                        <div className="text-sm font-medium text-cofeeNeutral10">
                          <CurrencyAmount
                            value={Number(afterDiscountData.fee_amount)}
                            symbolClass="!mr-0.5"
                          />
                        </div>
                      </div>
                    )}
                    {Number(afterDiscountData.fine_amount) > 0 && (
                      <div className="flex items-center justify-between py-1">
                        <div className="text-sm font-medium text-cofeeNeutral8">
                          Fine
                        </div>
                        <div className="text-sm font-medium text-cofeeNeutral10">
                          <CurrencyAmount
                            value={Number(afterDiscountData.fine_amount)}
                            symbolClass="!mr-0.5"
                          />
                        </div>
                      </div>
                    )}
                    {Number(afterDiscountData.total_gst_amount) > 0 && (
                      <div className="flex items-center justify-between py-1">
                        <div className="flex items-center text-sm font-medium text-cofeeNeutral8">
                          GST
                        </div>
                        <div className="text-sm font-medium text-cofeeNeutral10">
                          <CurrencyAmount
                            value={Number(afterDiscountData.total_gst_amount)}
                            symbolClass="!mr-0.5"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          {transactionData.action === 'close_payment' && (
            <div className="mt-[16px] flex w-full flex-row items-center gap-[6px] rounded-lg bg-cofeeYellow1 p-[12px]">
              <Icon
                name="alert-circle"
                stroke={theme.cofeeYellow7}
                size="medium"
              />
              <div className="leading-[20px] tracking-[-0.54px] text-cofeeNeutral10">
                {getInfoMessage()}
              </div>
            </div>
          )}
          <div className="pt-[16px]">
            <div className="pb-[4px] font-semibold leading-[20px] tracking-[-0.2px]">
              Notes
            </div>
            <Input
              placeholder="Enter notes, if any"
              value={note}
              handleInputChange={(value) => setNote(value)}
              maxLength={100}
              inputStyle="!bg-white !text-content"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            label="Confirm"
            handleClick={handleConfirmButtonClick}
            disabled={
              !!discountAmountError ||
              (transactionData.action === 'discount' && discountAmount === '')
            }
            // loading={false}
            className="mx-6 my-4 w-37.5"
            loading={isClosePaymentLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default UpdateDueModal;
